import React, { useCallback, useContext, useState } from 'react'
import { Transforms } from 'slate'
import { ReactEditor } from 'slate-react'
import { Card, CardContent, CardHeader, Dialog, DialogContent, DialogTitle, Icon, IconButton, cn, makeStyles } from '@cicero/ui'
import { EditorContext, IDashboardWidgetElement, IEditorElementProps, IElementDefinition } from '../EditorService'
import { DashboardCommands } from '../commands/DashboardCommands'
import { WidgetConfigForm } from '../widgets/config'

export const WidgetElement = ({ attributes, children, element }: IEditorElementProps<IDashboardWidgetElement>) => {
  const { widgetElement, widgetWrapper, widgetTitle, widgetContent, widgetFullWidth, widgetStickRight, widgetStickLeft } = useStyles()
  const [editing, setEditing] = useState(false)
  const service = useContext(EditorContext)

  const updateWidget = useCallback((widget: IDashboardWidgetElement) => {
    const path = ReactEditor.findPath(service.editor, element)
    Transforms.setNodes(service.editor, { ...widget }, { at: path })
    setEditing(false)
  }, [element, service])

  const deleteWidget = useCallback(() => {
    DashboardCommands.removeWidget(service.editor, element)
    setEditing(false)
  }, [element, service])

  return <span
    {...attributes}
    contentEditable={false}
    className={cn(widgetElement, {
      [widgetFullWidth]: element.size === 6 || element.stick === 'fill',
      [widgetStickRight]: element.stick === 'right',
      [widgetStickLeft]: element.stick === 'left',
    })}
  >
    <CardHeader
      className={widgetTitle}
      title={element.title}
      action={
        <span className="actionWrapper" contentEditable={false}>
          <IconButton onClick={() => setEditing(true)}><Icon>settings</Icon></IconButton>
        </span>
      }
    />
    <Card className={widgetWrapper} style={{ flexGrow: element.size || 1 }} variant="outlined" elevation={2}>
      <CardContent className={widgetContent}>
        { service.renderWidget(element) }
        <Dialog open={editing} onClose={() => setEditing(false)} maxWidth="xl" fullWidth={true}>
          <DialogTitle>Edit Widget</DialogTitle>
          <DialogContent>
            <WidgetConfigForm leaf={element} onCancel={() => setEditing(false)} onSave={(value) => updateWidget(value)} onDelete={() => deleteWidget()} />
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
    {children}
  </span>
}

export const WidgetDefaultRenderer = ({ type, leaf }: { type: string, leaf: any }) => <React.Fragment>
  <p>Widget Type &quot;{type}&quot; has no renderer.</p>
  <pre>{JSON.stringify(leaf, null, 2)}</pre>
</React.Fragment>

export const WidgetElementDef: IElementDefinition = {
  renderer(props) { return <WidgetElement {...props} /> },
  setup(service) {
    const { isVoid } = service.editor
    service.editor.isVoid = (el) => el.type === 'widget' ? true : isVoid(el)
  },
}

const useStyles = makeStyles({
  widgetElement: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    '& .actionWrapper': {
      opacity: 0,
      transition: 'all ease-in-out .2s',
    },
    '&:hover': {
      '& .actionWrapper': {
        opacity: 1,
      },
    },
  },
  widgetTitle: {
    margin: '0 6px',
    padding: 0,
    fontWeight: 200,
    '& .MuiTypography-displayBlock': {
      fontSize: '1.3em',
      fontWeight: 200,
      color: '#666',
    },
  },
  widgetWrapper: {
    minWidth: 400,
    minHeight: 300,
    borderRadius: 0,
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'stretch',
    alignItems: 'stretch',
  },
  widgetContent: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px !important',
  },
  widgetFullWidth: {
    display: 'block',
    minWidth: '100vw',
    marginBottom: 16,
  },
  widgetStickRight: {
    float: 'right',
    marginLeft: 16,
    marginBottom: 16,
    '@media screen and (max-width: 960px)': {
      display: 'block',
      marginLeft: 0,
      float: 'none',
    },
  },
  widgetStickLeft: {
    float: 'left',
    marginRight: 16,
    marginBottom: 16,
    '@media screen and (max-width: 960px)': {
      display: 'block',
      float: 'none',
      marginRight: 0,
    },
  },
})
